import { TiaCoreInputProps, useTiaInput } from './Forms'
import * as ThemeUI from 'theme-ui'
import { Label, Box, Flex } from '../'
import omit from 'lodash.omit'

// custom size for checkboxes based on check+pad
// but that doesn't play nice with SVGs
const checkboxSize = 40

interface CheckboxProps
    extends TiaCoreInputProps,
        Omit<ThemeUI.CheckboxProps, 'name' | 'onChange'> {
    /**
     * optionally turn checkbox into nice Tia checkbox with label
     */
    pretty?: boolean
}

// helper component for fancy Tia checkboxes that look like buttons
// following pattern from https://github.com/system-ui/theme-ui/blob/master/packages/components/src/Checkbox.js
const TiaCheckbox = (props: CheckboxProps & { register: any }) => {
    const { register } = props

    return (
        <Box sx={props.sx}>
            <Box
                ref={register}
                as="input"
                type="checkbox"
                {...omit(props, 'pretty')}
                sx={{
                    position: 'absolute',
                    opacity: 0,
                    zIndex: -1,
                    width: 1,
                    height: 1,
                    overflow: 'hidden'
                }}
            />
            <Flex
                sx={{
                    'border': (t: ThemeUI.Theme) =>
                        `1px solid ${t.colors?.inputOutline}`,
                    'background': 'white',
                    'borderRadius': 2,
                    'width': checkboxSize,
                    'height': checkboxSize,
                    'cursor': 'pointer',
                    'input:checked ~ & svg': {
                        display: 'block'
                    },
                    'input:checked ~ &': (t: ThemeUI.Theme) => ({
                        background: t.colors?.darkSecondaryButton
                    }),
                    '&:hover': {
                        borderColor: (t: ThemeUI.Theme) =>
                            t.colors?.inputOutlineHover
                    },
                    'mr': 4,
                    'alignItems': 'center',
                    'justifyContent': 'center'
                }}
            >
                <Box
                    as="svg"
                    sx={{
                        width: '18px',
                        height: '14px',
                        fill: 'none',
                        display: 'none'
                    }}
                >
                    <path
                        d="M1.5 7.29412L6.3 12L16.5 2"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </Box>
            </Flex>
        </Box>
    )
}

export const Checkbox = (props: CheckboxProps) => {
    const { error, borderColor, register } = useTiaInput(props)

    // by default we use ThemeUI's Chexkbox component
    // otherwise Tia's fancy checkbox
    let checkbox = props.pretty ? (
        <TiaCheckbox {...props} register={register} />
    ) : (
        <ThemeUI.Checkbox
            sx={{
                borderColor,
                ...props.sx
            }}
            {...omit(props, ['sx', 'pretty'])}
            ref={register}
        />
    )

    return (
        <>
            {checkbox}
            {error}
        </>
    )
}

/**
 * convenience component for typical labeled checkboxes
 * use children for label
 */
export const LabeledCheckbox = (props: CheckboxProps) => {
    return (
        <Label
            variant="forms.boldLabel"
            sx={{
                cursor: 'pointer',
                lineHeight: `${checkboxSize}px`
            }}
        >
            <Checkbox
                pretty
                name={props.name}
                {...omit(props, ['children', 'name'])}
            />
            {props.children}
        </Label>
    )
}
